/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { ArrowBack, Edit, Delete, Image } from "@mui/icons-material";
import { deleteAboutImage, getAboutImageById } from "actions";
import { clearError, clearCurrentAboutImage } from "features/aboutImagesSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { PATHNAME } from "constants/pathnames";
import Notify from "helpers/Notify";
import { NOTIFY } from "constants/notifyType";
import { FILE_TYPE } from "constants/fileTypes";
import "./AboutImages.scss";
import { BrandButton } from "components/brand";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

export const AboutImagesDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [deleteDialog, setDeleteDialog] = useState(false);

  const { currentAboutImage, loading, error } = useSelector(
    (state) => state.aboutImages,
  );

  const handleEdit = () => {
    navigate(`${PATHNAME.aboutImages}/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteDialog(true);
  };

  const handleDelete = () => {
    dispatch(deleteAboutImage(id));
    setDeleteDialog(false);
    navigate(PATHNAME.aboutImages);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    return `${imageBase}${image.path}`;
  };

  useEffect(() => {
    if (id) {
      dispatch(getAboutImageById(id));
    }

    return () => {
      dispatch(clearCurrentAboutImage());
    };
  }, [id]);

  useEffect(() => {
    if (error) {
      Notify(NOTIFY.type.error, error);
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentAboutImage) {
    return (
      <Box className="about-images-detail">
        <Alert severity="error">{ADMIN_TEXTS.aboutImages.imageNotFound}</Alert>
        <BrandButton
          className="back-button"
          onClick={() => navigate(PATHNAME.aboutImages)}
        >
          {ADMIN_TEXTS.common.back}
        </BrandButton>
      </Box>
    );
  }

  return (
    <Box className="about-images-detail">
      <Box className="detail-header">
        <IconButton onClick={() => navigate(-1)} className="back-button">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {ADMIN_TEXTS.aboutImages.imageDetails}
        </Typography>
      </Box>

      <Box className="detail-actions">
        <BrandButton
          variant="primary"
          startIcon={<Edit />}
          onClick={handleEdit}
          className="edit-button"
        >
          {ADMIN_TEXTS.aboutImages.editImage}
        </BrandButton>
        <BrandButton
          variant="error"
         
          startIcon={<Delete />}
          onClick={handleDeleteClick}
        >
          {ADMIN_TEXTS.aboutImages.deleteImage}
        </BrandButton>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {ADMIN_TEXTS.aboutImages.currentImage}
          </Typography>

          {currentAboutImage.image ? (
            <Paper className="image-container">
              {currentAboutImage?.image?.type === FILE_TYPE.VIDEO ? (
                <video
                  src={getImageUrl(currentAboutImage.image)}
                  className="detail-image"
                  controls
                />
              ) : (
                <img
                  src={getImageUrl(currentAboutImage.image)}
                  alt="About"
                  className="detail-image"
                />
              )}
            </Paper>
          ) : (
            <Paper className="no-image-container">
              <Image className="no-image-icon" />
              <Typography variant="h6" color="text.secondary">
                {ADMIN_TEXTS.aboutImages.noImage}
              </Typography>
            </Paper>
          )}

          <Box className="info-grid">
            <Box className="info-section">
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {ADMIN_TEXTS.common.order}
              </Typography>
              <Typography variant="body1">{currentAboutImage.order}</Typography>
            </Box>

            <Box className="info-section">
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {ADMIN_TEXTS.common.created}
              </Typography>
              <Typography variant="body1">
                {new Date(currentAboutImage.createdAt).toLocaleDateString()}
              </Typography>
            </Box>

            <Box className="info-section">
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {ADMIN_TEXTS.common.updated}
              </Typography>
              <Typography variant="body1">
                {new Date(currentAboutImage.updatedAt).toLocaleDateString()}
              </Typography>
            </Box>

            {currentAboutImage.image && (
              <>
                <Box className="info-section">
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ADMIN_TEXTS.common.fileName}
                  </Typography>
                  <Typography variant="body1">
                    {currentAboutImage.image.originalName}
                  </Typography>
                </Box>

                <Box className="info-section">
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ADMIN_TEXTS.common.fileSize}
                  </Typography>
                  <Typography variant="body1">
                    {(currentAboutImage.image.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>

                <Box className="info-section">
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {ADMIN_TEXTS.common.fileType}
                  </Typography>
                  <Typography variant="body1">
                    {currentAboutImage.image.mimeType}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>{ADMIN_TEXTS.aboutImages.deleteImage}</DialogTitle>
        <DialogContent>
          <Typography>{ADMIN_TEXTS.aboutImages.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton onClick={() => setDeleteDialog(false)} color="primary">
            {ADMIN_TEXTS.common.cancel}
          </BrandButton>
          <BrandButton onClick={handleDelete} variant="error">
            {ADMIN_TEXTS.common.delete}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
